import { IncomingMessage, ServerResponse } from 'http';
import {
  KameleoonCore,
  SdkLanguageType,
  KameleoonError,
  KameleoonException,
  VisitorCodeManager,
  IVisitorCodeManager,
  SetDataCallbackParametersType,
  Validator,
  KameleoonLogger,
  UrlProvider,
} from '@kameleoon/javascript-sdk-core';
import { RequesterMiddleware } from './requester';
import { DataStorage } from './storage';
import {
  SDKParameters,
  IExternalVisitorCodeManager,
  GetVisitorCodeParametersType,
  SetLegalConsentParametersType,
  IExternalNextJSVisitorCodeManager,
  SetNextJSLegalConsentParametersType,
  GetNextJSVisitorCodeParametersType,
  IExternalDenoVisitorCodeManager,
  IExternalCustomVisitorCodeManager,
  GetCustomVisitorCodeParametersType,
  GetDenoVisitorCodeParametersType,
  DenoMessage,
  SetDenoLegalConsentParametersType,
  SetCustomLegalConsentParametersType,
  Compatibility,
} from './types';
import { DEFAULT_CLEANUP_INTERVAL } from './constants';
import { KameleoonPRNG } from './prng';

/**
 * @class
 * KameleoonClient - a class for creating kameleoon client instance
 * */
export class KameleoonClient extends KameleoonCore {
  private readonly domain?: string;
  private readonly internalVisitorCodeManager: IVisitorCodeManager;
  private readonly externalVisitorCodeManager:
    | IExternalVisitorCodeManager
    | IExternalNextJSVisitorCodeManager
    | IExternalDenoVisitorCodeManager
    | IExternalCustomVisitorCodeManager;

  constructor({
    siteCode,
    credentials,
    configuration,
    compatibility,
    externals,
  }: SDKParameters) {
    const { clientId, clientSecret } = credentials;

    if (!clientId || !clientSecret) {
      throw new KameleoonError(KameleoonException.Credentials);
    }

    const storage = externals.storage || new DataStorage();
    const eventSource = externals.eventSource;
    const externalVisitorCodeManager = externals.visitorCodeManager;
    const sdkName = SdkLanguageType.NODEJS;
    const sdkVersion = process.env.npm_package_version as string;
    const urlProvider = new UrlProvider();

    const prng = new KameleoonPRNG();
    const visitorCodeManager = new VisitorCodeManager();
    const requesterMiddleware = new RequesterMiddleware(
      externals.requester,
      credentials,
      urlProvider,
    );

    const updatedConfiguration = {
      ...configuration,
      targetingDataCleanupInterval:
        configuration?.targetingDataCleanupInterval || DEFAULT_CLEANUP_INTERVAL,
    };

    const dependencies = {
      externalPRNG: prng,
      externalStorage: storage,
      externalEventSource: eventSource,
      externalRequester: requesterMiddleware,
      externalVisitorCodeManager: visitorCodeManager,
      externalPackageInfo: {
        type: sdkName,
        version: sdkVersion,
        isServer: true,
      },
      externalLogger: externals.logger,
    };

    const settings = {
      useAbortController: true,
    };

    switch (compatibility) {
      // --- Note ---
      // Falling through cases is intentional as former compatibility levels include the later ones
      // @ts-expect-error
      case Compatibility.Node12:
        if (dependencies.externalRequester.isKameleoonRequester) {
          throw new Error(
            'Node.js 12 doesn\'t support "@kameleoon/nodejs-requester", read more about compatibility in the documentation',
          );
        }
      case Compatibility.Node14:
        settings.useAbortController = false;
    }

    super({
      siteCode,
      configuration: updatedConfiguration,
      dependencies: {
        urlProvider,
      },
      internalConfiguration: {
        externals: dependencies,
        settings,
      },
    });

    const cookieDomain = Validator.validateDomain(configuration?.cookieDomain);
    this.domain = cookieDomain.ok ? cookieDomain.data : undefined;
    this.internalVisitorCodeManager = visitorCodeManager;
    this.externalVisitorCodeManager = externalVisitorCodeManager;
  }

  /**
   * @method initialize - an asynchronous method for KameleoonClient initialization by fetching Kameleoon SDK related data from server or by retrieving data from local source if data is up-to-date or update interval has not been reached
   * @returns {Promise<boolean>} Promise resolved into boolean field indicating success or fail
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.StorageWrite` Couldn't update storage data
   * - `KameleoonException.StorageRead` - Couldn't read storage data
   * - `KameleoonException.ClientConfiguration` Couldn't retrieve client configuration from Kameleoon Api
   * - `KameleoonException.MaximumRetriesReached` Maximum retries reached, request failed
   */
  public async initialize(): Promise<boolean> {
    return super.initialize();
  }

  /**
   * @method getVisitorCode - obtains visitor code from the request headers cookie, if the visitor code doesn't yet exist generates a random visitor code (or uses a provided default) and sets a new visitor code to response headers cookie
   * @param {GetVisitorCodeParametersType} params - an object consisting of the following fields: `request`, `response` and `defaultVisitorCode`
   * @returns {string} result visitor code
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public getVisitorCode({
    request,
    response,
    defaultVisitorCode,
  }: GetVisitorCodeParametersType): string;
  /**
   * @method getVisitorCode - retrieves or generates a visitor code using cookies.
   * @param {GetDenoVisitorCodeParametersType} params - an object consisting of the following fields: `request`, `response` and `defaultVisitorCode`
   * @returns {string} result visitor code
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public getVisitorCode({
    request,
    response,
    defaultVisitorCode,
  }: GetDenoVisitorCodeParametersType): string;
  /**
   * @method getVisitorCode - retrieves or generates a visitor code using cookies. Optimized for Next.js, it works with the state of cookies from `cookies()` in server-side contexts like API Routes or GetServerSideProps
   * @param {GetNextJSVisitorCodeParametersType} params - an object consisting of the following fields: `cookies` and `defaultVisitorCode`
   * @returns {string} result visitor code
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public getVisitorCode({
    cookies,
    defaultVisitorCode,
  }: GetNextJSVisitorCodeParametersType): string;
  /**
   * @method getVisitorCode - retrieves or generates a visitor code using input and output parameters. It's useful for custom implementations where the visitor code is stored in a different way than cookies
   * @param {GetCustomVisitorCodeParametersType} params - an object consisting of the following fields: `input`, `output` and `defaultVisitorCode`
   * @returns {string} result visitor code
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public getVisitorCode({
    input,
    output,
  }: GetCustomVisitorCodeParametersType): string;
  public getVisitorCode(
    parameters:
      | GetVisitorCodeParametersType
      | GetNextJSVisitorCodeParametersType
      | GetCustomVisitorCodeParametersType
      | GetDenoVisitorCodeParametersType,
  ): string {
    KameleoonLogger.info`CALL KameleoonClient.getVisitorCode(parameters: ${parameters})`;
    // --- NodeJS + NextJS (Server-Side Props) + Deno ---
    if ('request' in parameters && 'response' in parameters) {
      const { request, response } = parameters;

      // --- Deno ---
      if (request?.headers?.get) {
        this.internalVisitorCodeManager.setData = (
          parameters: SetDataCallbackParametersType,
        ) => {
          (
            this.externalVisitorCodeManager as IExternalDenoVisitorCodeManager
          ).setData({
            ...parameters,
            response: response as DenoMessage,
            domain: this.domain,
          });
        };

        this.internalVisitorCodeManager.getData = (key: string) => {
          return (
            this.externalVisitorCodeManager as IExternalDenoVisitorCodeManager
          ).getData({ request: request as DenoMessage, key });
        };
        // --- NodeJS/NextJS (Server-Side Props) ---
      } else {
        this.internalVisitorCodeManager.setData = (
          parameters: SetDataCallbackParametersType,
        ) => {
          (
            this.externalVisitorCodeManager as IExternalVisitorCodeManager
          ).setData({
            ...parameters,
            response: response as ServerResponse,
            domain: this.domain,
          });
        };

        this.internalVisitorCodeManager.getData = (key: string) => {
          return (
            this.externalVisitorCodeManager as IExternalVisitorCodeManager
          ).getData({ request: request as IncomingMessage, key });
        };
      }
    }

    // --- NextJS (Server Actions) ---
    if ('cookies' in parameters) {
      const { cookies } = parameters;

      this.internalVisitorCodeManager.setData = (
        parameters: SetDataCallbackParametersType,
      ) => {
        (
          this.externalVisitorCodeManager as IExternalNextJSVisitorCodeManager
        ).setData({
          ...parameters,
          domain: this.domain,
          cookies,
        });
      };

      this.internalVisitorCodeManager.getData = (key: string) => {
        return (
          this.externalVisitorCodeManager as IExternalNextJSVisitorCodeManager
        ).getData({ cookies, key });
      };
    }

    // --- Custom ---
    if ('input' in parameters && 'output' in parameters) {
      const { input, output } = parameters;

      this.internalVisitorCodeManager.setData = (
        parameters: SetDataCallbackParametersType,
      ) => {
        (
          this.externalVisitorCodeManager as IExternalCustomVisitorCodeManager
        ).setData({
          ...parameters,
          output,
          domain: this.domain,
        });
      };

      this.internalVisitorCodeManager.getData = (key: string) => {
        return (
          this.externalVisitorCodeManager as IExternalCustomVisitorCodeManager
        ).getData({ input, key });
      };
    }

    const visitorCode = this.internalVisitorCodeManager.getVisitorCode(
      parameters.defaultVisitorCode,
    );
    KameleoonLogger.info`RETURN KameleoonClient.getVisitorCode(parameters: ${parameters}) -> (visitorCode: ${visitorCode})`;
    return visitorCode;
  }

  /**
   * @method setLegalConsent - sets or updates the legal consent status for a visitor identified by their unique visitor code, affecting values in the response cookies based on the consent status
   * @param {SetLegalConsentParametersType} params - an object consisting of the following fields: `visitorCode`, `consent` and `response`
   * @returns {void}
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public setLegalConsent({
    visitorCode,
    consent,
    response,
  }: SetLegalConsentParametersType): void;
  /**
   * @method setLegalConsent - sets or updates the legal consent status for a visitor identified by their unique visitor code, affecting values in the response cookies based on the consent status
   * @param {SetDenoLegalConsentParametersType} params - an object consisting of the following fields: `visitorCode`, `consent` and `response`
   * @returns {void}
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public setLegalConsent({
    visitorCode,
    consent,
    response,
  }: SetDenoLegalConsentParametersType): void;
  /**
   * @method setLegalConsent - sets or updates the legal consent status for a visitor identified by their unique visitor code, affecting values in the response cookies based on the consent status
   * @param {SetNextJSLegalConsentParametersType} params - an object consisting of the following fields: `visitorCode`, `consent` and `cookies`
   * @returns {void}
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public setLegalConsent({
    visitorCode,
    consent,
    cookies,
  }: SetNextJSLegalConsentParametersType): void;
  /**
   * @method setLegalConsent - sets or updates the legal consent status for a visitor identified by their unique visitor code, affecting values in the output object based on the consent status
   * @param {SetCustomLegalConsentParametersType} params - an object consisting of the following fields: `visitorCode`, `consent` and `output`
   * @returns {void}
   * @throws `KameleoonError` with one of the following `type` s:
   *
   * - `KameleoonException.VisitorCodeMaxLength` - The visitor code length was exceeded
   * - `KameleoonException.VisitorCodeEmpty` The visitor code is empty
   */
  public setLegalConsent({
    visitorCode,
    consent,
    output,
  }: SetCustomLegalConsentParametersType): void;
  public setLegalConsent(
    parameters:
      | SetLegalConsentParametersType
      | SetNextJSLegalConsentParametersType
      | SetDenoLegalConsentParametersType
      | SetCustomLegalConsentParametersType,
  ): void {
    KameleoonLogger.info`CALL KameleoonClient.setLegalConsent(parameters: ${parameters})`;
    // --- NodeJS + NextJS (Server-Side Props) + Deno ---
    if ('response' in parameters) {
      const { visitorCode, consent, response } = parameters;

      // --- NodeJS/NestJS (Server-Side Actions) ---
      if ('setHeader' in response) {
        super.setUserConsent({
          visitorCode,
          consent,
          setData: (params: SetDataCallbackParametersType): void => {
            if (response) {
              (
                this.externalVisitorCodeManager as IExternalVisitorCodeManager
              ).setData({
                ...params,
                response,
                domain: this.domain,
              });
            }
          },
        });
        // --- Deno ---
      } else {
        super.setUserConsent({
          visitorCode,
          consent,
          setData: (params: SetDataCallbackParametersType): void => {
            (
              this.externalVisitorCodeManager as IExternalDenoVisitorCodeManager
            ).setData({
              ...params,
              response: response as DenoMessage,
              domain: this.domain,
            });
          },
        });
      }
    }

    // --- NextJS (Server Actions) ---
    if ('cookies' in parameters) {
      const { visitorCode, consent, cookies } = parameters;

      super.setUserConsent({
        visitorCode,
        consent,
        setData: (params: SetDataCallbackParametersType): void => {
          (
            this.externalVisitorCodeManager as IExternalNextJSVisitorCodeManager
          ).setData({
            ...params,
            domain: this.domain,
            cookies,
          });
        },
      });
    }

    // --- Custom ---
    if ('output' in parameters) {
      const { visitorCode, consent, output } = parameters;

      super.setUserConsent({
        visitorCode,
        consent,
        setData: (params: SetDataCallbackParametersType): void => {
          (
            this.externalVisitorCodeManager as IExternalCustomVisitorCodeManager
          ).setData({
            ...params,
            output,
            domain: this.domain,
          });
        },
      });
    }
    KameleoonLogger.info`RETURN KameleoonClient.setLegalConsent(parameters: ${parameters})`;
  }

  /**
   * Triggers a data file revalidation only when the current configuration is stale.
   *
   * If the data file is still valid and the last update happened less than
   * `updateInterval` ago, the method returns `false` and no update request is made.
   *
   * If the data file is stale, the method waits for the revalidation request to finish
   * and returns `true` when the request was performed. Returning `true` means that the
   * check was executed, but the configuration itself may still remain unchanged, for
   * example when the server reports that the current data file is up to date.
   *
   * In a usual long-lived Node.js runtime, using this method is generally not
   * recommended, because the SDK already keeps the data file fresh automatically during
   * initialization and normal runtime execution. It can still be helpful in edge-style
   * environments such as Cloudflare Workers, where runtime behavior is more short-lived
   * and revalidation may need to be triggered explicitly.
   *
   * @returns {Promise<boolean>} `true` if an update request was performed, `false` if
   * the current data file is still valid and no request was made.
   */
  public async refreshDataFileIfStale(): Promise<boolean> {
    return super._refreshDataFileIfStale();
  }
}
